import { BuildDomain, getPrimaryBuildTypeForBuildDomain } from 'src/enums/build/build-domain';
import { getLanguageUrlPrefixForBuildType } from 'src/enums/build/build-type';
import type { Language } from 'src/enums/language';
import abby from 'src/helpers/abby';
import { preparePath } from 'src/helpers/navigation-helper';
import puzzel from 'src/helpers/puzzel';
import { colors, fonts } from 'src/styling/constants';
import type { StaticTextLookupKey } from 'src/translators/static-text-lookup';
import { loadTranslator, type StaticTextTranslator } from 'src/translators/static-text-translator';

import { activeEnv } from '../../build-scripts/environment';

const COOKIE_POLICY_URL = '/policy/cookie-policy';
const COOKIE_POLICY_UPDATED = '22/01/2021';
const THIRD_PARTY_COOKIES_OPT_OUT_URL = '/policy/cookie-policy-management';

const ANALYTICAL_COOKIES_CONSENT_KEY = 'analytics';
const PREFERENCES_COOKIES_CONSENT_KEY = 'preferences';
const MARKETING_COOKIES_CONSENT_KEY = 'marketing';
const SOCIAL_MEDIA_COOKIES_CONSENT_KEY = 'social_media';

const COOKIE_CONSENT_CHANGED_EVENT_KEY = 'cookieConsentChanged';

let openConsentBanner: (() => void) | undefined = undefined;

export const initialiseCookieConsentBanner = async (
  pageLanguage: Language,
  buildDomain: BuildDomain
): Promise<void> => {
  const translate = await loadTranslator(pageLanguage);

  const config = getConfig(translate, pageLanguage, buildDomain);

  if (!window.CookieControl) {
    // eslint-disable-next-line no-console
    console.warn('CookieControl is missing');
    return;
  }

  window.CookieControl.load(config);
  openConsentBanner = window.CookieControl.open;
};

export const openCookieConsentBanner = (): void => {
  // There's inconsistency between browsers around when this function becomes available
  const open = openConsentBanner ?? window.CookieControl.open;

  if (open === undefined) {
    // eslint-disable-next-line no-console
    console.warn('Cookie banner cannot be opened: not initialised yet.');
    return;
  }

  open();
};

const getApiKeyAndProductFor = (buildDomain: BuildDomain): [string, 'PRO' | 'PRO_MULTISITE'] => {
  switch (buildDomain) {
    case BuildDomain.MERIDIAN:
      return ['4da3aba3b97ec9fe4ddc80ba51035ff195768a6e', 'PRO'];
    case BuildDomain.TOGETHER_FOUNDATION:
      return ['3540cb8fa1d108ec613c23a0712686276fe12e98', 'PRO'];
    default:
      return ['9b91d04b99e26753df1505f1bc6d66f2ed074f58', 'PRO_MULTISITE'];
  }
};

const getConfig = (translate: StaticTextTranslator, lang: Language, buildDomain: BuildDomain) => {
  const [apiKey, product] = getApiKeyAndProductFor(buildDomain);
  return {
    apiKey,
    product,
    initialState: 'notify',
    rejectButton: shouldIncludeRejectAllButtonFor(buildDomain),
    notifyDismissButton: false,
    settingsStyle: 'button',
    closeOnGlobalChange: true,
    position: 'left',
    theme: 'light',
    closeStyle: 'button',
    mode: 'GDPR',
    secureCookie: true,

    text: {
      title: translate('cookieConsentBannerTitle'),
      intro: translate('cookieConsentBannerIntro'),
      accept: translate('cookieConsentBannerAccept'),
      acceptSettings: translate('cookieConsentBannerAcceptSettings'),
      reject: translate('cookieConsentBannerReject'),
      rejectSettings: translate('cookieConsentBannerRejectSettings'),
      settings: translate('cookieConsentBannerSettings'),
      necessaryTitle: translate('cookieConsentBannerNecessaryTitle'),
      necessaryDescription: translate('cookieConsentBannerNecessaryDescription'),
      on: translate('cookieConsentBannerOn'),
      off: translate('cookieConsentBannerOff'),
      thirdPartyTitle: translate('cookieConsentBannerThirdPartyTitle'),
      thirdPartyDescription: translate('cookieConsentBannerThirdPartyDescription'),
      closeLabel: translate('cookieConsentBannerCloseLabel'),
      notifyTitle: translate('cookieConsentBannerNotifyTitle'),
      notifyDescription: translate('cookieConsentBannerNotifyDescription'),
    },
    statement: {
      description: translate('cookieConsentBannerStatementDescription'),
      name: translate('cookieConsentBannerStatementName'),
      url: getCookiePolicyUrl(buildDomain, lang),
      updated: COOKIE_POLICY_UPDATED,
    },
    branding: getBranding(buildDomain),

    necessaryCookies: getNecessaryCookies(),

    optionalCookies: [
      {
        name: ANALYTICAL_COOKIES_CONSENT_KEY,
        label: translateStaticText(translate, 'cookieConsentBannerOptionalCookiesAnalyticsLabel'),
        description: translateStaticText(
          translate,
          'cookieConsentBannerOptionalCookiesAnalyticsDescription'
        ),
        cookies: getAnalyticsCookies(),
        thirdPartyCookies: getThirdPartyCookies(),
        onAccept: () => handleAnalyticsCookieChange({ analytics_accepted: true }),
        onRevoke: () => handleAnalyticsCookieChange({ analytics_accepted: false }),
      },
      {
        name: PREFERENCES_COOKIES_CONSENT_KEY,
        label: translateStaticText(translate, 'cookieConsentBannerOptionalCookiesPreferencesLabel'),

        description: translateStaticText(
          translate,
          'cookieConsentBannerOptionalCookiesPreferencesDescription'
        ),

        cookies: getPreferencesCookies(),
        thirdPartyCookies: getThirdPartyCookies(),
        onAccept: () => handleConsentCookieChange({ preferences_accepted: true }),
        onRevoke: () => handleConsentCookieChange({ preferences_accepted: false }),
      },
      {
        name: MARKETING_COOKIES_CONSENT_KEY,
        label: translateStaticText(translate, 'cookieConsentBannerOptionalCookiesMarketingLabel'),

        description: translateStaticText(
          translate,
          'cookieConsentBannerOptionalCookiesMarketingDescription'
        ),
        cookies: getMarketingCookies(),
        thirdPartyCookies: getThirdPartyCookies(),
        onAccept: () => handleConsentCookieChange({ marketing_accepted: true }),
        onRevoke: () => handleConsentCookieChange({ marketing_accepted: false }),
      },
      {
        name: SOCIAL_MEDIA_COOKIES_CONSENT_KEY,
        label: translateStaticText(translate, 'cookieConsentBannerOptionalCookiesSocialMediaLabel'),

        description: translateStaticText(
          translate,
          'cookieConsentBannerOptionalCookiesSocialMediaDescription'
        ),

        cookies: getSocialMediaCookies(),
        thirdPartyCookies: getThirdPartyCookies(),
        onAccept: () => handleConsentCookieChange({ social_media_accepted: true }),
        onRevoke: () => handleConsentCookieChange({ social_media_accepted: false }),
      },
    ],
  };
};

const translateStaticText = (translate: StaticTextTranslator, translateKey: StaticTextLookupKey) =>
  JSON.stringify(translate(translateKey));

const shouldIncludeRejectAllButtonFor = (buildDomain: BuildDomain): boolean => {
  if (activeEnv === 'development') {
    return true;
  }

  switch (buildDomain) {
    case BuildDomain.DAVID_LLOYD_BELGIUM:
    case BuildDomain.DAVID_LLOYD_ITALY:
    case BuildDomain.DAVID_LLOYD_NETHERLANDS:
    case BuildDomain.DAVID_LLOYD_SPAIN:
    case BuildDomain.DAVID_LLOYD_SWITZERLAND:
    case BuildDomain.DAVID_LLOYD_GERMANY:
    case BuildDomain.MERIDIAN:
    case BuildDomain.TOGETHER_FOUNDATION:
    case BuildDomain.DAVID_LLOYD_LUXEMBOURG:
      return true;
    case BuildDomain.DAVID_LLOYD_FRANCE:
    case BuildDomain.DAVID_LLOYD_IRELAND:
    case BuildDomain.DAVID_LLOYD_UK:
    case BuildDomain.HARBOUR:
      return false;
  }
};

const getCookiePolicyUrl = (buildDomain: BuildDomain, language: Language) => {
  // Note: cookie policy pages don't use a translated /policy/ slug

  const languagePrefix = getLanguageUrlPrefixForBuildType(
    language,
    getPrimaryBuildTypeForBuildDomain(buildDomain)
  );
  const path = `/${languagePrefix}/${COOKIE_POLICY_URL}/`;

  return preparePath(path);
};

const getBranding = (buildDomain: BuildDomain) => {
  const commonBrandingSettings = {
    fontSizeTitle: '1.4em',
    fontSizeHeaders: '1.2em',
    removeAbout: true,
    removeIcon: true,
  };

  switch (buildDomain) {
    case BuildDomain.HARBOUR: {
      const harbourFontColor = colors.harbourSlateGrey;
      return {
        ...commonBrandingSettings,
        fontColor: harbourFontColor,
        fontFamily: fonts.harbour.fontFamily,
        fontSize: '1em',
        notifyFontColor: harbourFontColor,
        backgroundColor: colors.davidLloydGray,
        toggleText: harbourFontColor,
        toggleColor: harbourFontColor,
        toggleBackground: colors.lightGray,
        alertText: harbourFontColor,
        alertBackground: colors.greyD,
        acceptText: colors.defaultPromoTextColor,
        acceptBackground: harbourFontColor,
        rejectText: harbourFontColor,
      };
    }
    case BuildDomain.MERIDIAN: {
      const meridianFontColor = colors.meridianBlue;
      return {
        ...commonBrandingSettings,
        fontColor: meridianFontColor,
        fontFamily: fonts.regular.fontFamily,
        fontSize: '0.9rem',
        notifyFontColor: meridianFontColor,
        backgroundColor: '#FFFFFF',
        toggleText: meridianFontColor,
        toggleColor: colors.meridianOrange,
        toggleBackground: colors.lightGray,
        alertText: meridianFontColor,
        alertBackground: colors.lightGray,
        acceptText: '#FFFFFF',
        acceptBackground: colors.meridianOrange,
        rejectText: meridianFontColor,
      };
    }
    default: {
      const davidLloydFontColor = colors.davidLloydBrown;
      const camelot = colors.camelot;
      return {
        ...commonBrandingSettings,
        fontColor: davidLloydFontColor,
        fontFamily: fonts.regular.fontFamily,
        fontSize: '0.9rem',
        notifyFontColor: davidLloydFontColor,
        backgroundColor: colors.lightGray,
        toggleText: davidLloydFontColor,
        toggleColor: camelot,
        toggleBackground: colors.greyD,
        alertText: davidLloydFontColor,
        alertBackground: colors.greyD,
        acceptText: colors.defaultPromoTextColor,
        acceptBackground: camelot,
        rejectText: davidLloydFontColor,
      };
    }
  }
};

const getNecessaryCookies = () => [
  '__RequestVerificationToken',
  'ASP.NET_SessionId',
  '__cfduid',
  'JSESSIONID',
  'X-Mapping-#',
  '.ASPXAUTH',
  'SC_ANALYTICS_GLOBAL_COOKIE',
  '__RequestVerificationToken',
  '_grecaptcha',
  '_GRECAPTCHA',
  'test_cookie',
  // Convert:
  '_conv_sptest',
  '_conv_check_cookies',
  'convert_optout',
];

const getAnalyticsCookies = () => [
  'HasEnquired',
  'IsMember',
  'david-lloyd_new-club-page-experience',
  'FormTracking',
  '__qca',
  '_dc_gtm_UA-#',
  '_ga',
  '_gat',
  '_gid',
  '_hjid',
  '_hjTLDTest',
  'bx_bucket_number',
  '_hjAbsoluteSessionInProgress',
  '_hjIncludedInSessionSample',
  '_hjIncludedInPageViewSample',
  'AnalyticsSyncHistory',
  'oo',
  'https://tracking.monsido.com/',
  '_ga_Q17WCLZRSB',
  '_ga_886PT6X2T7',
  '_ga_5HXT2C1V3E',
  '_ga_XBEGQYYEGB',
  '_ga_0MS8JYT181',
  '_ga_Y05DCCJE8P',
  '_ga_WP8VL08V82',
  '_gcl_au',
  '_ga_L277XMBJKG',
  '_ga_VSFNPTKSK0',
  '_ga_B671ER93TS',
  // Convert:
  '_conv_v',
  '_conv_s',
  '_conv_r',
  '_conv_t',
];

const getPreferencesCookies = () => [
  'davidlloydbe#lang',
  'davidlloydde#lang',
  'davidlloydes#lang',
  'davidlloydfr#lang',
  'davidlloydie#lang',
  'davidlloydit#lang',
  'davidlloydnl#lang',
  'davidlloyduk#lang',
  'davidlloydorg#lang',
  '@@scroll#',
];

const getMarketingCookies = () => [
  '_fbp',
  '_gcl_au',
  '_pin_unauth',
  '_hjIncludedInSample',
  '_hjRecordingEnabled',
  'adm.sesh',
  'adm.usmeta',
  'bid_dlhvjhttfcspzofprwbdxbat2qc2ryvq',
  'bStorage',
  'tag',
  '_psegs',
  'ck',
  'CMRUM3',
  'mako_uid',
  'match',
  'match/bounce',
  'mc',
  'oo',
  'pcs/activeview',
  'permutive-id',
  'permutive-session',
  'pt',
  'tr',
  'track/pxl',
];

const getSocialMediaCookies = (): unknown[] => [];

const getThirdPartyCookies = () => [
  {
    name: '',
    optOutLink: THIRD_PARTY_COOKIES_OPT_OUT_URL,
  },
];

type OnAnalyticsCookieConsentChangeArgs = { analytics_accepted: boolean };
export type OnCookieConsentChangeArgs =
  | OnAnalyticsCookieConsentChangeArgs
  | { preferences_accepted: boolean }
  | { marketing_accepted: boolean }
  | { social_media_accepted: boolean };

const handleConsentCookieChange = (consentChange: OnCookieConsentChangeArgs): void => {
  if (!window) {
    return;
  }

  abby.allowedAllConsentCookieOnChange(consentChange);
  puzzel.allowedAllConsentCookieOnChange(consentChange);

  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push(Object.assign({ event: COOKIE_CONSENT_CHANGED_EVENT_KEY }, consentChange));
};

const handleAnalyticsCookieChange = (consentChange: OnAnalyticsCookieConsentChangeArgs): void => {
  allowAmplitudeEvents(consentChange.analytics_accepted);
  allowedConvertTestingTool(consentChange.analytics_accepted);
  handleConsentCookieChange(consentChange);
};

const allowedConvertTestingTool = (allow: boolean): void => {
  if (!window) {
    return;
  }

  window._conv_q = window._conv_q || [];

  window._conv_q.push({
    what: allow ? 'consentGiven' : 'consentRequired',
  });
};

const allowAmplitudeEvents = (allow: boolean): void => {
  if (!window) {
    return;
  }
  window.launchAmplitude =
    window.launchAmplitude ||
    function () {
      return undefined;
    };

  window.launchAmplitude({ allow });
};
